import * as React from "react";
import { observer, inject } from "mobx-react";
import { Link, withRouter, RouteComponentProps, RouteProps } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import Select from "react-select";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faExternalLinkSquareAlt, faEdit, faPenSquare, faTrash } from "@fortawesome/free-solid-svg-icons";

import LoadingWidget from "../common/LoadingWidget";
import { CustomerLink } from "../common/CustomerLink";
import { HorseLink } from "../common/HorseLink";

import { HorseForSaleListStore } from "../../stores/HorseForSaleListStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

import {
    SimpleHorseForSale,
    HorseForSale,
    HorseForSaleState,
    HorseForSaleStateFriendlyName,
    HorseForSaleStateOptions
} from "../../model/HorseForSale";
import { SimpleCustomer } from "src/model/Customer";
import { HorseSale } from "src/model/HorseForSale";
import { SimpleHorse, HorseGait } from "src/model/Horse";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { successMessage, errorMessage } from "../common/Toast";

export interface OptionType {
    value: string;
    label: string;
}
export interface Props extends RouteComponentProps<any> {
    horseForSaleListStore?: HorseForSaleListStore;
    configurationStore?: ConfigurationStore;
}
export interface State {
    isLoading: boolean;
}

@inject("configurationStore")
@inject("horseForSaleListStore")
@observer
export class HorsesForSaleList extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isLoading: false
        };
    }

    handleSearchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        this.props.horseForSaleListStore!.updateHorseForSaleListFilters({ fullTextSearch: e.target.value });
    };

    onChangeSelectState = (e: Array<OptionType>): void => {
        const selectedStates = e.map((state: any) => state.value);
        this.props.horseForSaleListStore!.updateHorseForSaleListFilters({ selectedStates });
    };

    onChangeSelectSale = (e: Array<OptionType>): void => {
        const selectedSales = e
            .map((sale: any) => sale.value)
            .map(saleId => this.props.configurationStore!.findHorseSaleById(parseInt(saleId, 10)))
            .filter(sale => sale !== null) as Array<HorseSale>;
        this.props.horseForSaleListStore!.updateHorseForSaleListFilters({ selectedSales });
    };

    onChangeSelectGait = (e: Array<OptionType>): void => {
        const selectedGaits = e
            .map((sale: any) => sale.value)
            .map(gaitId => this.props.configurationStore!.findHorseGaitById(parseInt(gaitId, 10)))
            .filter(sale => sale !== null) as Array<HorseGait>;
        this.props.horseForSaleListStore!.updateHorseForSaleListFilters({ selectedGaits });
    };

    tableChanged = (type: TableChangeType, event: TableChangeNewState) => {
        if (type === "pagination") {
            this.props.horseForSaleListStore!.updateHorseForSaleListState({
                page: event.page,
                sizePerPage: event.sizePerPage
            });
        } else if (type === "sort") {
            this.props.horseForSaleListStore!.updateHorseForSaleListState({
                sortOrder: event.sortOrder,
                sortField: event.sortField as any
            });
        }
    };

    deleteHorseForSale = (horseForSale: SimpleHorseForSale) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        event.stopPropagation();

        try {
            const goAhead = await SimpleConfirmation(`Are you sure you'd like to delete ${horseForSale.horse.name} from ${horseForSale.sale.name}?`);
            if (!goAhead) {
                return;
            }

            await this.props.horseForSaleListStore!.deleteHorseForSale(horseForSale.id);
            successMessage(`${horseForSale.horse.name} has been deleted from ${horseForSale.sale.name}.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    componentDidMount() {
        this.props.horseForSaleListStore!.resetAndRefresh();
    }

    render() {
        const { horseForSaleListStore } = this.props;
        if (!horseForSaleListStore) {
            return <div>No Horses For Sale found.</div>;
        }
        const { horseForSaleListFilters, horseForSaleListState } = horseForSaleListStore;

        const { configurationStore } = this.props;
        if (!configurationStore) {
            return <div>No Sale or Gair or Sale State found.</div>;
        }

        const columns = [
            {
                dataField: "sale.name",
                text: "Sale",
                order: "asc"
            },
            {
                dataField: "hipNumber",
                text: "Hip #",
                sort: true
            },
            {
                dataField: "horse",
                text: "Name",
                formatter: (cellContent: SimpleHorse, row: any) => <HorseLink targetBlank={true} horse={row.horse} />
            },
            {
                dataField: "horse.gait.name",
                text: "Gait"
            },
            {
                dataField: "state",
                text: "Sale State",
                formatter: (state: HorseForSaleState) => HorseForSaleStateFriendlyName(state)
            },
            {
                dataField: "price",
                text: "Price",
                formatter: (cellContent: any, row: HorseForSale) => {
                    return row.price ? (row.price / 100).toLocaleString("en-CA", { style: "currency", currency: "CAD" }) : "-";
                }
            },
            {
                dataField: "contact",
                text: "Contact (for inspection)",
                formatter: (cellContent: SimpleCustomer, row: any) =>
                    row.contact && <CustomerLink customer={row.contact} includePhone={true} targetBlank={true} />
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "80px", textAlign: "center" },
                formatter: (cellContent: HorseForSale, row: any) => {
                    return (
                        <div className="btn-group btn-group-toggle ml-3">
                            <Link
                                to={`/horses-for-sale/${row.id}/application`}
                                className="btn btn-secondary btn-sm"
                                id={`tooltipOpenH4Sale${row.id}`}
                                title="Open Horse For Sale"
                            >
                                <FontAwesomeIcon icon={faPenSquare} />
                            </Link>
                            <UncontrolledTooltip placement="top" target={`tooltipOpenH4Sale${row.id}`} delay={0} className="tooltipstrap">
                                Open Horse For Sale
                            </UncontrolledTooltip>
                            <button
                                onClick={this.deleteHorseForSale(row)}
                                className="btn btn-secondary btn-sm"
                                id={`tooltipDeleteH4S${row.id}`}
                                title="Delete Horse For Sale"
                                disabled={row.referenced}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDeleteH4S${row.id}`} delay={0} className="tooltipstrap">
                                Delete Horse For Sale
                            </UncontrolledTooltip>
                        </div>
                    );
                }
            }
        ];

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Horses for Sale
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-12">
                            <h1>Horse For Sale List</h1>
                        </div>
                    </div>
                    <div className="card shadow-sm mr-4">
                        <div className="card-body">
                            <form className="">
                                <div className="form-row">
                                    <div className="col-12">
                                        <div className="form-row">
                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label htmlFor="firstname">Search</label>
                                                    <div className="input-group">
                                                        <div className="input-group-prepend">
                                                            <div className="input-group-text">
                                                                <FontAwesomeIcon icon={faSearch} />
                                                            </div>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id="firstname"
                                                            placeholder="Search..."
                                                            name="firstName"
                                                            value={horseForSaleListFilters.fullTextSearch}
                                                            onChange={this.handleSearchOnChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="col-12">
                                        <label htmlFor="lastname">State</label>
                                        <Select
                                            name="roles"
                                            options={HorseForSaleStateOptions()}
                                            value={HorseForSaleStateOptions().filter(
                                                opt => horseForSaleListFilters.selectedStates.findIndex(s => s === opt.value) >= 0
                                            )}
                                            onChange={this.onChangeSelectState}
                                            isMulti={true}
                                        />
                                    </div>
                                </div>
                                <div className="form-row mt-3">
                                    <div className="col-6">
                                        <label htmlFor="lastname">Sale</label>
                                        <Select
                                            name="sales"
                                            options={configurationStore.horseSales.map(sale => {
                                                return {
                                                    label: sale.name,
                                                    value: sale.id + ""
                                                };
                                            })}
                                            onChange={this.onChangeSelectSale}
                                            isMulti={true}
                                        />
                                    </div>
                                    <div className="col-6">
                                        <label htmlFor="lastname">Gait</label>
                                        <Select
                                            name="gait"
                                            options={configurationStore.horsesGaits.map(gait => {
                                                return {
                                                    label: gait.name,
                                                    value: gait.id + ""
                                                };
                                            })}
                                            onChange={this.onChangeSelectGait}
                                            isMulti={true}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="row mt-4">
                        <div className="col-12 ml-3">
                            Total Horses For Sale: <strong>{horseForSaleListStore.horseForSaleList.total}</strong>
                        </div>
                    </div>
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="table-responsive">
                                {
                                    <BootstrapTable
                                        keyField="id"
                                        remote={true}
                                        hover={true}
                                        bordered={false}
                                        columns={columns}
                                        bootstrap4={true}
                                        data={horseForSaleListStore.horseForSaleList.results}
                                        pagination={paginationFactory({
                                            page: horseForSaleListState.page,
                                            sizePerPage: horseForSaleListState.sizePerPage,
                                            totalSize: horseForSaleListStore.horseForSaleList.total
                                        })}
                                        onTableChange={this.tableChanged}
                                        defaultSorted={[
                                            {
                                                dataField: "hipNumber",
                                                order: "asc"
                                            }
                                        ]}
                                        noDataIndication={() => (
                                            <div>{horseForSaleListStore.loading ? <LoadingWidget /> : <span>No results found.</span>}</div>
                                        )}
                                    />
                                }
                                {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                    Otherwise the `noDataIndication` wouldn't work */}
                                {horseForSaleListStore.loading && <div />}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter<Props>(HorsesForSaleList);



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/HorsesForSaleList.tsx